import { styled } from "@pigment-css/react";
import { MarkdownBody } from "../../ui";
import { MarkdownRenderer } from "@acto/taskpane/core/components/markdown/MarkdownRenderer";
import { Flex, Divider, Chip } from "../../../primitives";
import { useTranslations } from "@acto/web-common/locales";
import { ChevronRight } from "@acto/web-common/components";

interface CustomerRecapSectionProps {
  date: string;
  content: string;
  completedRecapCount: number;
  onRecapCountClick: () => void;
}

const SectionTitle = styled("h3")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[100],
  margin: 0,
}));

const DateText = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[56],
}));

const MarkdownWrapper = styled(MarkdownBody)(({ theme }) => ({
  "& p": {
    margin: 0,
    marginBottom: theme.managementView.spacing[12],
  },

  "& p:last-child": {
    marginBottom: 0,
  },
}));

const RecapCountButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[16]}`,
  width: `calc(100% + ${theme.managementView.spacing[16]})`,
  marginLeft: `-${theme.managementView.spacing[16]}`,
  backgroundColor: theme.managementView.colors.neutral[0],
  border: "none",
  borderRadius: theme.managementView.radius[16],
  cursor: "pointer",
  transition: "background-color 0.2s",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  "& svg": {
    width: "10px",
    height: "10px",
    color: theme.managementView.colors.neutral[56],
  },
}));

const RecapCountText = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[90],
}));

export const CustomerRecapSection = ({
  date,
  content,
  completedRecapCount,
  onRecapCountClick,
}: CustomerRecapSectionProps) => {
  const t = useTranslations();

  return (
    <>
      <Flex direction="column" gap={18}>
        <SectionTitle>{t("managementView.customerDetails.recap")}</SectionTitle>
        <Flex direction="column" gap={4}>
          <DateText>{date}</DateText>
          <MarkdownWrapper>
            <MarkdownRenderer content={content} />
          </MarkdownWrapper>
        </Flex>
      </Flex>

      <Divider />

      <RecapCountButton onClick={onRecapCountClick}>
        <Flex align="center" gap={8}>
          <Chip size="large" color="default">
            {completedRecapCount}
          </Chip>
          <RecapCountText>{t("managementView.customerDetails.completedRecaps")}</RecapCountText>
        </Flex>
        <ChevronRight />
      </RecapCountButton>

      <Divider />
    </>
  );
};
